'use client'

import { ClientLabel } from 'labels/client'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { AllPurchasesError } from 'my-purchases/components/AllPurchasesError/AllPurchasesError'
import { EmptyInactivePurchases } from 'my-purchases/components/EmptyInactivePurchases/EmptyInactivePurchases'
import { InactivePurchasesButton } from 'my-purchases/components/InactivePurchasesButton/InactivePurchasesButton'
import { Loading } from 'my-purchases/components/Loading/Loading'
import { PurchaseList } from 'my-purchases/components/PurchaseList/PurchaseList'
import { PURCHASES_ERROR_TYPES } from 'my-purchases/constants/purchasesErrorTypes'
import { NoPurchasesContainer } from 'my-purchases/containers/NoPurchasesContainer/NoPurchasesContainer'
import { usePurchases } from 'my-purchases/hooks/usePurchases/usePurchases'
import {
	sendOnClickPreviousPurchases,
	sendOnLoadingError,
} from 'my-purchases/utils/analytics/analyticsEvents'
import { SnackbarStatus } from 'notifications/constants/constants'
import { useNotificationsContext } from 'notifications/hooks/useNotificationsContext'
import { useEffect, useMemo, useState } from 'react'
import { useSession } from 'session/src/hooks/useSession'

import text from 'fukku/styles/texts.module.scss'
import styles from './PurchasesListContainer.module.scss'

export function PurchasesListContainer() {
	const { t } = useLabels()
	const { name } = useSession()
	const [forceLoadInactivePurchases, setForceLoadInactivePurchases] =
		useState(false)

	const [showNotification, setShowNotification] = useState(false)

	const {
		errors,
		activePurchases,
		inactivePurchases,
		reloadActivePurchases,
		reloadInactivePurchases,
		isActivePurchasesLoading,
		isInActivePurchasesLoading,
	} = usePurchases(forceLoadInactivePurchases)

	const { addNotification } = useNotificationsContext()

	const {
		activePurchasesError,
		inactivePurchasesError,
		showNoPurchasesContainer,
		showFullPageError,
		showInactivePurchasesButton,
		showActivePurchasesList,
		showInactivePurchaseList,
		showEmptyInactivePurchases,
		showGeneralLoading,
	} = useMemo(() => {
		const activePurchasesErrorValue = errors?.find((x) => x.isActivePurchases)
		const inactivePurchasesErrorValue = errors?.find(
			(x) => !x.isActivePurchases
		)
		const showNoPurchasesContainerValue = errors?.find(
			(x) => x.errorType === PURCHASES_ERROR_TYPES.NO_PURCHASES_FOUND
		)
		const showFullPageErrorValue = errors?.find(
			(x) => x.errorType === PURCHASES_ERROR_TYPES.FULL_PAGE_ERROR
		)
		const inactivePurchasesEmpty = inactivePurchases?.length === 0

		const showActivePurchases =
			activePurchasesErrorValue || !!activePurchases?.length

		return {
			activePurchasesError: activePurchasesErrorValue,
			inactivePurchasesError: inactivePurchasesErrorValue,
			showNoPurchasesContainer: showNoPurchasesContainerValue,
			showFullPageError: showFullPageErrorValue,
			showInactivePurchasesButton:
				(activePurchases || activePurchasesErrorValue) &&
				!inactivePurchases &&
				!inactivePurchasesErrorValue,
			showInactivePurchaseList:
				inactivePurchasesErrorValue || !!inactivePurchases?.length,
			showActivePurchasesList: showActivePurchases,
			showEmptyInactivePurchases:
				!inactivePurchasesErrorValue && inactivePurchasesEmpty,
			showGeneralLoading:
				isActivePurchasesLoading ||
				(isInActivePurchasesLoading && !showActivePurchases),
		}
	}, [errors, activePurchases, inactivePurchases])

	useEffect(() => {
		if (inactivePurchasesError && !showNotification) {
			setShowNotification(true)
			addNotification({
				id: 'myPurchases.snackbar.showHistory.error',
				title: t('myPurchases.snackbar.showHistory.error.title'),
				subtitle: t('myPurchases.snackbar.showHistory.error.subtitle'),
				status: SnackbarStatus.INFO,
				onClose: () => setShowNotification(false),
			})
		}
	}, [inactivePurchasesError, showNotification])

	useEffect(() => {
		if (isInActivePurchasesLoading) {
			setShowNotification(false)
		}
	}, [isInActivePurchasesLoading])

	if (showGeneralLoading) {
		return <Loading />
	}

	if (showNoPurchasesContainer) {
		return <NoPurchasesContainer />
	}

	const reloadAllPurchases = () => {
		reloadActivePurchases()
		reloadInactivePurchases()
	}

	if (showFullPageError) {
		sendOnLoadingError()
		return <AllPurchasesError retryCallback={reloadAllPurchases} />
	}

	const handleInactivePurchasesClick = () => {
		sendOnClickPreviousPurchases()
		setForceLoadInactivePurchases(true)
		reloadInactivePurchases()
	}
	return (
		<div className={styles.container}>
			<p className={`${text.bodyM} ${styles.subtitle}`}>
				<ClientLabel placeholderValues={{ 1: name ?? '' }}>
					{t('myPurchases.general.subtitle')}
				</ClientLabel>
			</p>

			{showActivePurchasesList && (
				<PurchaseList
					title='my_purchases.active_purchases.subtitle'
					purchases={activePurchases}
					retryFn={reloadActivePurchases}
					error={activePurchasesError}
					isActive
				/>
			)}

			{showInactivePurchasesButton && (
				<InactivePurchasesButton
					handleClick={handleInactivePurchasesClick}
					isLoading={isInActivePurchasesLoading}
				/>
			)}

			{showInactivePurchaseList && (
				<PurchaseList
					title='my_purchases.subtitle.purchase_history'
					purchases={inactivePurchases}
					retryFn={reloadInactivePurchases}
					error={inactivePurchasesError}
					isActive={false}
				/>
			)}

			{showEmptyInactivePurchases && <EmptyInactivePurchases />}
		</div>
	)
}
